import { NOMBRE_APP } from '../config/marca';

/**
 * Errores de Firebase Auth en castellano. Firebase los da en inglés y con un
 * código entre paréntesis («Firebase: Error (auth/wrong-password).»), que no
 * le dice nada a nadie.
 */
const MENSAJES: Record<string, string> = {
  'auth/invalid-credential':     'Email o contraseña incorrectos.',
  'auth/wrong-password':         'Contraseña incorrecta.',
  'auth/user-not-found':         'No hay ninguna cuenta con ese email.',
  'auth/invalid-email':          'Ese email no es válido.',
  'auth/email-already-in-use':   'Ya hay una cuenta con ese email. Inicia sesión con ella.',
  'auth/weak-password':          'La contraseña debe tener al menos 6 caracteres.',
  'auth/too-many-requests':      'Demasiados intentos seguidos. Espera un poco y vuelve a probar.',
  'auth/network-request-failed': 'Sin conexión. Comprueba tu red y vuelve a probar.',
  'auth/popup-blocked':          'El navegador ha bloqueado la ventana de acceso. Permite las ventanas emergentes para esta web.',
  'auth/operation-not-allowed':  'Este método de acceso no está activado todavía.',
  'auth/unauthorized-domain':    'Este dominio no está autorizado para iniciar sesión.',
  'auth/user-disabled':          'Esta cuenta está desactivada.',
  'auth/credential-already-in-use': `Esa cuenta ya está vinculada a otro usuario de ${NOMBRE_APP}.`,
  'auth/provider-already-linked':   'Ese método ya está vinculado a tu cuenta.',
  'auth/no-such-provider':          'Ese método no estaba vinculado.',
  'auth/requires-recent-login':     'Por seguridad, vuelve a identificarte para hacer esto.',
  'auth/user-mismatch':             'Has entrado con otra cuenta distinta de la tuya. Usa la misma con la que estás conectado.',
  'auth/invalid-api-key':          'La app no tiene bien configurada la conexión con Firebase (falta o no vale la clave).',
  'auth/account-exists-with-different-credential':
    'Ya tienes una cuenta con ese email. Entra como lo hacías hasta ahora y quedará vinculada.',
  'auth/missing-password':        'Escribe la contraseña.',
  'auth/missing-email':           'Escribe el email.',
  'auth/invalid-password':        'La contraseña debe tener al menos 6 caracteres.',
  'auth/user-token-expired':      'Tu sesión ha caducado. Vuelve a iniciar sesión.',
  'auth/expired-action-code':     'Ese enlace ha caducado. Pide uno nuevo.',
  'auth/invalid-action-code':     'Ese enlace ya no vale: puede que ya se haya usado. Pide uno nuevo.',
  'auth/quota-exceeded':          'Se han hecho demasiadas peticiones. Vuelve a probar dentro de un rato.',
  'auth/timeout':                 'El servidor ha tardado demasiado en responder. Vuelve a probar.',
  'auth/internal-error':          'Ha fallado algo en el servidor de acceso. Vuelve a probar.',
  'auth/web-storage-unsupported': 'Este navegador no deja guardar la sesión (¿modo privado?). Prueba en una ventana normal.',
  'auth/admin-restricted-operation': 'Esta operación no está permitida.',
};

/** Cerrar la ventana de Apple o Google no es un error: el usuario se lo ha pensado. */
export function esCancelacion(e: unknown): boolean {
  const code = (e as { code?: string })?.code;
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
}

export function mensajeErrorAuth(e: unknown, porDefecto = 'No se ha podido completar el acceso.'): string {
  const code = (e as { code?: string })?.code;
  if (code && MENSAJES[code]) return MENSAJES[code];
  if (code === 'auth/password-does-not-meet-requirements') return mensajeContrasenaNoCumple(e);
  // La clave de Firebase limitada por dominio rechaza la app de iOS, que no
  // tiene dominio (capacitor://localhost)
  if (code?.startsWith('auth/requests-from-referer')) {
    return 'La clave de Firebase no admite peticiones desde la app. Hay que quitarle la restricción de dominio.';
  }
  const msg = (e as { message?: string })?.message;
  // Los errores propios (p. ej. del borrado de cuenta) ya vienen en castellano
  if (msg && !msg.startsWith('Firebase:')) return msg;
  // El código entre paréntesis no le dice nada a nadie, pero sin él no hay
  // forma de saber qué ha fallado cuando alguien manda una captura
  return code ? `${porDefecto} (${code})` : porDefecto;
}

/**
 * Errores al entrar o vincular con Apple o Google. «Email o contraseña
 * incorrectos» no tiene sentido aquí: invalid-credential significa que el
 * proveedor dijo que sí y Firebase rechazó su identificación (casi siempre
 * configuración: en la app de iOS, que Firebase no tenga registrada una app
 * de iOS con ese identificador de paquete, o un nonce que no cuadra). El
 * motivo exacto viene del servidor dentro del mensaje, entre «Firebase: » y
 * el código: se enseña para poder diagnosticarlo con una captura.
 */
export function mensajeErrorAccesoSocial(e: unknown, proveedor: string, porDefecto?: string): string {
  const code = (e as { code?: string })?.code;
  if (code !== 'auth/invalid-credential') return mensajeErrorAuth(e, porDefecto);
  const msg = (e as { message?: string })?.message ?? '';
  const detalle = msg.match(/^Firebase: (.+?) \(auth\//)?.[1];
  const base = `${proveedor} ha confirmado que eres tú, pero ${NOMBRE_APP} no ha podido aceptar su identificación.`;
  return detalle && detalle !== 'Error' ? `${base} (${detalle})` : `${base} (${code})`;
}

// Lo que Firebase dice que falta en la contraseña, en castellano
const FALTAS: Array<[RegExp, string]> = [
  [/upper case/i, 'una letra mayúscula'],
  [/lower case/i, 'una letra minúscula'],
  // Antes que «numeric», que también aparece dentro de «non-alphanumeric»
  [/non-alphanumeric/i, 'un símbolo'],
  [/numeric/i, 'un número'],
  [/at least (\d+) characters/i, 'al menos $1 caracteres'],
  [/at most (\d+) characters/i, 'como mucho $1 caracteres'],
];

/**
 * Traduce «Missing password requirements: [Password must contain an upper case
 * character, ...]», que es lo que Firebase manda con
 * auth/password-does-not-meet-requirements.
 */
export function mensajeContrasenaNoCumple(e: unknown): string {
  const msg = (e as { message?: string })?.message ?? '';
  const dentro = msg.match(/\[(.+)\]/)?.[1] ?? '';
  const faltas = dentro.split(',').flatMap(parte => {
    for (const [patron, texto] of FALTAS) {
      const m = parte.match(patron);
      if (m) return [texto.replace('$1', m[1] ?? '')];
    }
    return [];
  });
  if (!faltas.length) return 'La contraseña no cumple los requisitos de seguridad.';
  const lista = faltas.length === 1 ? faltas[0] : `${faltas.slice(0, -1).join(', ')} y ${faltas[faltas.length - 1]}`;
  return `La contraseña necesita ${lista}.`;
}
